import React, { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapView } from "@/components/MapView";
import { toast } from "sonner";
import { Search, MapPin, ArrowRight, Loader2, Plane, ShieldCheck, Box, CalendarDays } from "lucide-react";

export default function FindTravellers() {
  const [filters, setFilters] = useState({ origin: "", destination: "", date: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/trips", { params });
      setItems(data.trips);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openRequest = async (trip) => {
    setRequesting(trip);
    setSelectedDelivery("");
    try {
      const { data } = await api.get("/deliveries/mine");
      setMyDeliveries(data.deliveries.filter((d) => d.status === "Requested"));
    } catch { setMyDeliveries([]); }
  };

  const submitRequest = async () => {
    if (!selectedDelivery) { toast.error("Select one of your delivery requests"); return; }
    setSending(true);
    try {
      await api.post("/connections", { delivery_id: selectedDelivery, trip_id: requesting.id });
      toast.success("Your request has been sent.");
      setRequesting(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="Find Travellers" subtitle="Discover travellers heading your way who can carry your package." />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end" data-testid="find-travellers-filters">
        <div className="space-y-1.5"><Label className="text-xs">From</Label><Input data-testid="ft-origin" value={filters.origin} onChange={(e) => setFilters((f) => ({ ...f, origin: e.target.value }))} placeholder="Origin" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">To</Label><Input data-testid="ft-destination" value={filters.destination} onChange={(e) => setFilters((f) => ({ ...f, destination: e.target.value }))} placeholder="Destination" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Travel Date</Label><Input type="date" data-testid="ft-date" value={filters.date} onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))} className="rounded-xl" /></div>
        <Button onClick={load} data-testid="ft-search-btn" className="rounded-xl h-10"><Search className="w-4 h-4" /> Search</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={Plane} title="No travellers found." description="Try widening your search or check back soon." testid="empty-find-travellers" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="find-travellers-results">
          {items.map((t) => (
            <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`traveller-card-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-primary font-bold">{t.traveller?.name?.[0] || "?"}</div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 flex items-center gap-1.5 truncate">{t.traveller?.name}
                    {t.traveller?.verification_status === "verified" && <ShieldCheck className="w-4 h-4 text-emerald-500" />}
                  </p>
                  <p className="text-xs text-slate-400 capitalize">{t.traveller?.verification_status || "unverified"}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-800">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" /><span className="truncate">{t.origin}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{t.destination}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {t.travel_date}</span>
                <span className="flex items-center gap-1"><Box className="w-3.5 h-3.5" /> {t.capacity}</span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => setViewing(t)} data-testid={`view-trip-${t.id}`} className="rounded-xl flex-1">View Trip</Button>
                <Button onClick={() => openRequest(t)} data-testid={`request-delivery-${t.id}`} className="rounded-xl flex-1">Request Delivery</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View trip */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="view-trip-dialog">
          <DialogHeader><DialogTitle>{viewing?.traveller?.name}'s trip</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm"><span className="text-slate-400">Route: </span><span className="font-medium">{viewing?.origin} → {viewing?.destination}</span></div>
            <div className="text-sm"><span className="text-slate-400">Date: </span>{viewing?.travel_date} {viewing?.travel_time}</div>
            <div className="text-sm"><span className="text-slate-400">Capacity: </span>{viewing?.capacity}</div>
            {viewing?.notes && <div className="text-sm"><span className="text-slate-400">Notes: </span>{viewing?.notes}</div>}
            {(viewing?.origin_lat != null || viewing?.dest_lat != null) && (
              <MapView origin={viewing?.origin_lat != null ? { lat: viewing.origin_lat, lng: viewing.origin_lng } : null}
                destination={viewing?.dest_lat != null ? { lat: viewing.dest_lat, lng: viewing.dest_lng } : null} height={200} />
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => { setViewing(null); openRequest(viewing); }} className="rounded-xl w-full" data-testid="view-trip-request-btn">Request Delivery</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request delivery */}
      <Dialog open={!!requesting} onOpenChange={(o) => !o && setRequesting(null)}>
        <DialogContent data-testid="request-delivery-dialog">
          <DialogHeader><DialogTitle>Request this traveller</DialogTitle></DialogHeader>
          {myDeliveries.length === 0 ? (
            <p className="text-sm text-slate-500">You have no open delivery requests. Create a delivery first, then request a traveller.</p>
          ) : (
            <>
              <p className="text-sm text-slate-500">Choose which of your delivery requests to send to {requesting?.traveller?.name}.</p>
              <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                <SelectTrigger data-testid="request-delivery-select" className="rounded-xl"><SelectValue placeholder="Select a delivery" /></SelectTrigger>
                <SelectContent>{myDeliveries.map((d) => <SelectItem key={d.id} value={d.id}>{d.description} ({d.pickup} → {d.destination})</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequesting(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitRequest} disabled={sending || myDeliveries.length === 0} data-testid="request-delivery-submit-btn" className="rounded-xl">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
